/**
 * Aveda OS REST transport + endpoint manifest.
 *
 * The ERP's supported external surface is `{apiUrl}/api/v1/*` guarded by
 * workspace-scoped `av_live_…` API keys. This module mirrors the Aveda OS CLI's
 * `requestJson`/`normalizeBaseUrl` (packages/werkflow-cli/src/client.mjs) and
 * exposes a complete manifest of every `/api/v1` route plus a fail-safe risk
 * classifier used by the MCP approval broker.
 */

export type WerkflowRequestMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

export type WerkflowRestRisk = "read" | "low-risk-write" | "approval-required";

export interface WerkflowRestRequest {
  readonly method: WerkflowRequestMethod;
  /** Path under `/api/v1`, e.g. `/customers` or `/customers/abc123`. */
  readonly path: string;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined | null>>;
  readonly body?: unknown;
}

export interface WerkflowRestInvokerConfig {
  /** Base ERP URL. Normalized (`.convex.cloud` → `.convex.site`, trailing slash stripped). */
  readonly apiUrl: string;
  /** Aveda OS workspace API key (`av_live_…`). Sent as `Authorization: Bearer <apiKey>`. */
  readonly apiKey: string;
  readonly fetch?: typeof fetch | undefined;
}

export interface WerkflowRestResult {
  readonly status: number;
  readonly value: unknown;
}

/**
 * Structured error thrown by {@link WerkflowRestInvoker.request} on non-2xx
 * responses (or transport failures with `status === 0`).
 */
export class WerkflowRestError extends Error {
  readonly status: number;
  readonly value: unknown;
  /** Seconds to wait before retrying, parsed from the `Retry-After` header (429). */
  readonly retryAfterSeconds: number | undefined;

  constructor(options: {
    message: string;
    status: number;
    value?: unknown;
    retryAfterSeconds?: number | undefined;
  }) {
    super(options.message);
    this.name = "WerkflowRestError";
    this.status = options.status;
    this.value = options.value ?? null;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
}

const SECRET_PATTERN = /(?:wf|av)_live_[a-f0-9]+_[a-f0-9]+/gi;

/** Redact legacy and current Aveda OS API secrets for safe logging. */
export function redactWerkflowSecrets(value: string): string {
  return value.replace(SECRET_PATTERN, (secret) =>
    secret.toLowerCase().startsWith("av_") ? "av_live_***" : "wf_live_***",
  );
}

/**
 * Normalize an ERP base URL: strip trailing slashes and rewrite the Convex
 * cloud host to its HTTP-actions (`.convex.site`) counterpart. Mirrors
 * `normalizeBaseUrl` in the ERP CLI (`client.mjs:9`).
 */
export function normalizeWerkflowApiUrl(value: string): string {
  if (!value) return "";
  return value
    .trim()
    .replace(/\/+$/, "")
    .replace(/\.convex\.cloud$/, ".convex.site");
}

function buildWerkflowUrl(apiUrl: string, path: string, query: WerkflowRestRequest["query"]): URL {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`/api/v1${cleanPath}`, apiUrl);
  if (query) {
    for (const [key, raw] of Object.entries(query)) {
      if (raw === undefined || raw === null || raw === "") continue;
      url.searchParams.set(key, String(raw));
    }
  }
  return url;
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  return Number.isFinite(seconds) ? seconds : undefined;
}

/**
 * REST client for the Aveda OS `/api/v1` surface. Handles JSON
 * encode/decode, bearer auth, and structured error mapping (including 429
 * `Retry-After`).
 */
export class WerkflowRestInvoker {
  private readonly apiUrl: string;
  private readonly apiKey: string;
  private readonly fetchImpl: typeof fetch;

  constructor(config: WerkflowRestInvokerConfig) {
    this.apiUrl = normalizeWerkflowApiUrl(config.apiUrl);
    this.apiKey = config.apiKey;
    this.fetchImpl = config.fetch ?? fetch;
    if (!this.apiUrl) {
      throw new Error("Aveda OS API URL is required.");
    }
    if (!this.apiKey) {
      throw new Error("Aveda OS API key is required.");
    }
  }

  async request(req: WerkflowRestRequest): Promise<WerkflowRestResult> {
    const url = buildWerkflowUrl(this.apiUrl, req.path, req.query);
    const hasBody = req.body !== undefined;

    const headers: Record<string, string> = { Authorization: `Bearer ${this.apiKey}` };
    const init: RequestInit = { method: req.method, headers };
    if (hasBody) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(req.body);
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, init);
    } catch (cause) {
      throw new WerkflowRestError({
        message: `Aveda OS request failed: ${redactWerkflowSecrets(String((cause as Error)?.message ?? cause))}`,
        status: 0,
      });
    }

    const text = await response.text();
    let value: unknown = null;
    if (text) {
      try {
        value = JSON.parse(text);
      } catch {
        value = text;
      }
    }

    if (!response.ok) {
      const errorMessage =
        (isRecord(value) && isRecord(value.error) && typeof value.error.message === "string"
          ? value.error.message
          : undefined) ?? `HTTP ${response.status}`;
      throw new WerkflowRestError({
        message: redactWerkflowSecrets(errorMessage),
        status: response.status,
        value,
        retryAfterSeconds:
          response.status === 429
            ? parseRetryAfter(response.headers.get("retry-after"))
            : undefined,
      });
    }

    return { status: response.status, value };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ---------------------------------------------------------------------------
// Endpoint manifest
// ---------------------------------------------------------------------------

export interface WerkflowEndpoint {
  readonly method: WerkflowRequestMethod;
  /** Template with `:param` segments, e.g. `/customers/:id`. */
  readonly pathTemplate: string;
  readonly summary: string;
  /** German operator-facing label, e.g. `Kunden auflisten`. */
  readonly label: string;
  readonly risk: WerkflowRestRisk;
}

/** Every write scope needed by the documented Aveda OS REST surface. */
export const WERKFLOW_WRITE_SCOPES: ReadonlyArray<string> = [
  "customers:write",
  "leads:write",
  "projects:write",
  "billing:write",
  "payments:write",
  "planning:write",
  "scheduling:write",
  "documents:write",
  "project-files:write",
  "project-activity:write",
  "communication:write",
  "files:write",
  "automations:write",
  "settings:write",
  "tasks:write",
  "time:write",
  "absences:write",
];

/**
 * Return write scopes absent from an API key's `/me` response.
 * `*`, `*:write`, and `<domain>:*` are accepted as scope wildcards.
 */
export function missingWerkflowWriteScopes(scopes: ReadonlyArray<unknown>): ReadonlyArray<string> {
  const granted = new Set(
    scopes
      .filter((scope): scope is string => typeof scope === "string")
      .map((scope) => scope.trim()),
  );
  if (granted.has("*") || granted.has("*:write")) return [];
  return WERKFLOW_WRITE_SCOPES.filter((scope) => {
    const domain = scope.slice(0, scope.indexOf(":"));
    return !granted.has(scope) && !granted.has(`${domain}:*`);
  });
}

const read = (
  method: WerkflowRequestMethod,
  pathTemplate: string,
  summary: string,
  label: string,
): WerkflowEndpoint => ({ method, pathTemplate, summary, label, risk: "read" });

const lowRiskWrite = (
  method: WerkflowRequestMethod,
  pathTemplate: string,
  summary: string,
  label: string,
): WerkflowEndpoint => ({ method, pathTemplate, summary, label, risk: "low-risk-write" });

const approvalRequired = (
  method: WerkflowRequestMethod,
  pathTemplate: string,
  summary: string,
  label: string,
): WerkflowEndpoint => ({ method, pathTemplate, summary, label, risk: "approval-required" });

/**
 * Complete manifest of every `/api/v1` route. Source of truth:
 * ERP `.agents/skills/werkflow-api-cli/references/endpoints.md` and
 * `packages/werkflow-cli/src/commands.mjs`.
 *
 * Risk rules: every GET is `read`. A curated allowlist of low-risk writes
 * (task create/toggle/set-status, lead create, crm-activity create,
 * project-activity create, time-entry create, absence-request create,
 * planning-assignment create/move/update, files upload-url, automations
 * enable/disable) is `low-risk-write`. Every other non-GET route is
 * `approval-required` (fail-safe).
 */
export const WERKFLOW_ENDPOINTS: ReadonlyArray<WerkflowEndpoint> = [
  // Meta
  read("GET", "/me", "Read the API key identity, workspace, and granted scopes.", "Zugang prüfen"),

  // Customers
  read("GET", "/customers", "List customers.", "Kunden auflisten"),
  read("GET", "/customers/:id", "Read one customer.", "Kunde ansehen"),
  approvalRequired("POST", "/customers", "Create a customer.", "Kunde anlegen"),
  approvalRequired("PATCH", "/customers/:id", "Update a customer.", "Kunde bearbeiten"),
  approvalRequired("DELETE", "/customers/:id", "Archive a customer.", "Kunde archivieren"),

  // Leads
  read("GET", "/leads", "List leads.", "Leads auflisten"),
  read("GET", "/leads/:id", "Read one lead.", "Lead ansehen"),
  lowRiskWrite("POST", "/leads", "Create a lead.", "Lead anlegen"),
  approvalRequired("PATCH", "/leads/:id", "Update a lead.", "Lead bearbeiten"),
  approvalRequired(
    "POST",
    "/leads/:id/convert",
    "Convert a lead into customer and project.",
    "Lead in Kunde umwandeln",
  ),

  // Projects
  read("GET", "/projects", "List projects.", "Aufträge auflisten"),
  read("GET", "/projects/:id", "Read one project.", "Auftrag ansehen"),
  approvalRequired("PATCH", "/projects/:id", "Update a project.", "Auftrag bearbeiten"),

  // Billing
  read("GET", "/billing-documents", "List billing documents.", "Belege auflisten"),
  read("GET", "/billing-documents/:id", "Read one billing document.", "Beleg ansehen"),
  read("GET", "/billing-documents/:id/pdf", "Read a billing document PDF.", "Beleg-PDF ansehen"),
  approvalRequired("POST", "/billing-documents", "Create a billing document.", "Beleg erstellen"),
  approvalRequired(
    "PATCH",
    "/billing-documents/:id",
    "Update a billing document draft.",
    "Beleg-Entwurf bearbeiten",
  ),
  approvalRequired(
    "POST",
    "/billing-documents/:id/finalize",
    "Finalize a billing document.",
    "Rechnung stellen",
  ),
  approvalRequired(
    "POST",
    "/billing-documents/:id/mark-sent",
    "Mark a billing document as sent.",
    "Beleg als versendet markieren",
  ),

  // Payments
  read("GET", "/payments", "List payments.", "Zahlungen auflisten"),
  read("GET", "/payments/:id", "Read one payment.", "Zahlung ansehen"),
  approvalRequired("POST", "/payments", "Record a payment.", "Zahlung erfassen"),

  // Dunnings
  read("GET", "/dunnings", "List dunnings.", "Mahnungen auflisten"),
  read("GET", "/dunnings/:id", "Read one dunning.", "Mahnung ansehen"),

  // Accounting
  read(
    "GET",
    "/accounting/config",
    "Read accounting configuration.",
    "Buchhaltungs-Konfiguration ansehen",
  ),
  read(
    "GET",
    "/accounting/bookings",
    "List accounting bookings for a date range.",
    "Buchungen auflisten",
  ),
  read(
    "GET",
    "/accounting/vat-summary",
    "Read the VAT summary for a date range.",
    "USt-Übersicht ansehen",
  ),

  // Bank
  read(
    "GET",
    "/bank/open-invoices",
    "List open invoices for bank reconciliation.",
    "Offene Rechnungen auflisten",
  ),

  // Articles
  read("GET", "/articles", "List articles.", "Artikel auflisten"),
  read("GET", "/articles/:id", "Read one article.", "Artikel ansehen"),

  // Inventory
  read("GET", "/inventory-items", "List inventory items.", "Lagerartikel auflisten"),
  read("GET", "/inventory-items/:id", "Read one inventory item.", "Lagerartikel ansehen"),
  read(
    "GET",
    "/inventory-transactions",
    "List inventory transactions.",
    "Lagerbewegungen auflisten",
  ),
  read(
    "GET",
    "/inventory-reservations",
    "List inventory reservations.",
    "Lagerreservierungen auflisten",
  ),

  // Suppliers
  read("GET", "/suppliers", "List suppliers.", "Lieferanten auflisten"),
  read("GET", "/suppliers/:id", "Read one supplier.", "Lieferant ansehen"),

  // Purchase orders
  read("GET", "/purchase-orders", "List purchase orders.", "Bestellungen auflisten"),
  read("GET", "/purchase-orders/:id", "Read one purchase order.", "Bestellung ansehen"),

  // Time entries
  read("GET", "/time-entries", "List time entries.", "Zeiterfassungen auflisten"),
  read("GET", "/time-entries/:id", "Read one time entry.", "Zeiterfassung ansehen"),
  lowRiskWrite("POST", "/time-entries", "Create a time entry.", "Zeit erfassen"),

  // Absence requests
  read("GET", "/absence-requests", "List absence requests.", "Abwesenheitsanträge auflisten"),
  read("GET", "/absence-requests/:id", "Read one absence request.", "Abwesenheitsantrag ansehen"),
  lowRiskWrite("POST", "/absence-requests", "Create an absence request.", "Abwesenheit beantragen"),

  // Team members
  read("GET", "/team-members", "List team members.", "Teammitglieder auflisten"),

  // Tasks
  read("GET", "/tasks", "List tasks.", "Aufgaben auflisten"),
  read("GET", "/tasks/:id", "Read one task.", "Aufgabe ansehen"),
  lowRiskWrite("POST", "/tasks", "Create a task.", "Aufgabe anlegen"),
  approvalRequired("PATCH", "/tasks/:id", "Update a task.", "Aufgabe bearbeiten"),
  lowRiskWrite("POST", "/tasks/:id/set-status", "Set a task status.", "Aufgabenstatus setzen"),
  lowRiskWrite("POST", "/tasks/:id/toggle", "Toggle a task done state.", "Aufgabe abhaken"),
  approvalRequired("DELETE", "/tasks/:id", "Remove a task.", "Aufgabe löschen"),

  // CRM activities
  read("GET", "/crm-activities", "List CRM activities.", "CRM-Aktivitäten auflisten"),
  read("GET", "/crm-activities/:id", "Read one CRM activity.", "CRM-Aktivität ansehen"),
  lowRiskWrite("POST", "/crm-activities", "Create a CRM activity.", "CRM-Aktivität erfassen"),

  // Communication channels
  read(
    "GET",
    "/communication-channels",
    "List communication channels.",
    "Kommunikationskanäle auflisten",
  ),
  read(
    "GET",
    "/communication-channels/:id",
    "Read one communication channel.",
    "Kommunikationskanal ansehen",
  ),

  // Project activities
  read("GET", "/project-activities", "List project activities.", "Auftragsaktivitäten auflisten"),
  lowRiskWrite(
    "POST",
    "/project-activities",
    "Create a project activity.",
    "Auftragsaktivität erfassen",
  ),

  // Project files
  read("GET", "/project-files", "List project files.", "Auftragsdateien auflisten"),
  approvalRequired(
    "POST",
    "/project-files",
    "Attach a file to a project.",
    "Datei an Auftrag anhängen",
  ),
  approvalRequired(
    "DELETE",
    "/project-files/:id",
    "Remove a project file.",
    "Auftragsdatei entfernen",
  ),

  // Planning
  read("GET", "/planning/full", "Read the full planning board.", "Planungstafel ansehen"),
  approvalRequired("POST", "/planning/mode", "Change the planning mode.", "Planungsmodus ändern"),
  read(
    "GET",
    "/planning-assignments",
    "List planning assignments.",
    "Planungszuweisungen auflisten",
  ),
  lowRiskWrite(
    "POST",
    "/planning-assignments",
    "Create a planning assignment.",
    "Planungszuweisung anlegen",
  ),
  lowRiskWrite(
    "PATCH",
    "/planning-assignments/:id",
    "Update a planning assignment.",
    "Planungszuweisung bearbeiten",
  ),
  lowRiskWrite(
    "POST",
    "/planning-assignments/:id/move",
    "Move a planning assignment.",
    "Planungszuweisung verschieben",
  ),
  approvalRequired(
    "DELETE",
    "/planning-assignments/:id",
    "Remove a planning assignment.",
    "Planungszuweisung entfernen",
  ),
  read("GET", "/planning-teams", "List planning teams.", "Planungsteams auflisten"),
  read("GET", "/planning-teams/:id", "Read one planning team.", "Planungsteam ansehen"),
  approvalRequired("POST", "/planning-teams", "Create a planning team.", "Planungsteam anlegen"),
  approvalRequired(
    "PATCH",
    "/planning-teams/:id",
    "Update a planning team.",
    "Planungsteam bearbeiten",
  ),
  approvalRequired(
    "DELETE",
    "/planning-teams/:id",
    "Remove a planning team.",
    "Planungsteam entfernen",
  ),
  read("GET", "/planning-vehicles", "List planning vehicles.", "Planungsfahrzeuge auflisten"),
  read("GET", "/planning-vehicles/:id", "Read one planning vehicle.", "Planungsfahrzeug ansehen"),
  approvalRequired(
    "POST",
    "/planning-vehicles",
    "Create a planning vehicle.",
    "Planungsfahrzeug anlegen",
  ),
  approvalRequired(
    "PATCH",
    "/planning-vehicles/:id",
    "Update a planning vehicle.",
    "Planungsfahrzeug bearbeiten",
  ),
  approvalRequired(
    "DELETE",
    "/planning-vehicles/:id",
    "Remove a planning vehicle.",
    "Planungsfahrzeug entfernen",
  ),
  read("GET", "/planning-equipment", "List planning equipment.", "Planungsgeräte auflisten"),
  read("GET", "/planning-conflicts", "List planning conflicts.", "Planungskonflikte auflisten"),
  approvalRequired(
    "POST",
    "/planning-conflicts/:id/dismiss",
    "Dismiss a planning conflict.",
    "Planungskonflikt verwerfen",
  ),

  // Booking links
  approvalRequired("POST", "/booking-links", "Create a booking link.", "Buchungslink erstellen"),

  // Documents
  read("GET", "/documents", "List documents.", "Dokumente auflisten"),
  read("GET", "/documents/:id", "Read one document.", "Dokument ansehen"),
  approvalRequired("POST", "/documents", "Create a document.", "Dokument anlegen"),
  approvalRequired("PATCH", "/documents/:id", "Update a document.", "Dokument bearbeiten"),
  approvalRequired("DELETE", "/documents/:id", "Remove a document.", "Dokument entfernen"),
  approvalRequired(
    "POST",
    "/documents/:id/attach-file",
    "Attach a file to a document.",
    "Datei an Dokument anhängen",
  ),
  read(
    "GET",
    "/documents/:id/file-url",
    "Read a document file URL.",
    "Dokument-Datei-Link ansehen",
  ),

  // Files
  lowRiskWrite("POST", "/files/upload-url", "Mint a signed upload URL.", "Upload-Link erzeugen"),
  read(
    "GET",
    "/files/:storageId/url",
    "Read a signed download URL for a stored file.",
    "Download-Link ansehen",
  ),

  // Automations
  read("GET", "/automations/catalog", "List the automations catalog.", "Workflow-Katalog ansehen"),
  read("GET", "/automations/runs", "List automation runs.", "Workflow-Läufe auflisten"),
  lowRiskWrite("POST", "/automations/:key/enable", "Enable an automation.", "Workflow aktivieren"),
  lowRiskWrite(
    "POST",
    "/automations/:key/disable",
    "Disable an automation.",
    "Workflow deaktivieren",
  ),
  approvalRequired(
    "POST",
    "/automations/:key/save-config",
    "Save an automation configuration.",
    "Workflow konfigurieren",
  ),

  // Settings
  read("GET", "/settings", "Read workspace settings.", "Einstellungen ansehen"),
  approvalRequired("PATCH", "/settings", "Update workspace settings.", "Einstellungen ändern"),
];

export interface WerkflowRequestClassification {
  readonly risk: WerkflowRestRisk;
  readonly endpoint: WerkflowEndpoint | null;
}

function normalizeMethod(method: string): WerkflowRequestMethod {
  return method.toUpperCase() as WerkflowRequestMethod;
}

function splitPath(path: string): ReadonlyArray<string> {
  const withoutQuery = path.split("?")[0] ?? path;
  return withoutQuery.split("/").filter((segment) => segment.length > 0);
}

function templateMatches(
  template: ReadonlyArray<string>,
  concrete: ReadonlyArray<string>,
): boolean {
  if (template.length !== concrete.length) return false;
  for (let i = 0; i < template.length; i += 1) {
    const templateSegment = template[i]!;
    if (templateSegment.startsWith(":")) continue;
    if (templateSegment !== concrete[i]) return false;
  }
  return true;
}

/**
 * Match a concrete `(method, path)` against the endpoint manifest. Unknown
 * routes are classified `approval-required` (fail-safe), so an unrecognized
 * write can never bypass the approval broker.
 */
export function classifyWerkflowRequest(
  method: string,
  path: string,
): WerkflowRequestClassification {
  const normalizedMethod = normalizeMethod(method);
  const concreteSegments = splitPath(path);
  for (const endpoint of WERKFLOW_ENDPOINTS) {
    if (endpoint.method !== normalizedMethod) continue;
    if (templateMatches(splitPath(endpoint.pathTemplate), concreteSegments)) {
      return { risk: endpoint.risk, endpoint };
    }
  }
  return { risk: "approval-required", endpoint: null };
}

/**
 * Extract concrete `:param` values from a `path` by aligning it with its
 * endpoint `pathTemplate` (e.g. template `/automations/:key/save-config` and
 * path `/automations/offer_followup/save-config` → `{ key: "offer_followup" }`).
 */
function extractPathParams(pathTemplate: string, path: string): Readonly<Record<string, string>> {
  const templateSegments = splitPath(pathTemplate);
  const concreteSegments = splitPath(path);
  if (templateSegments.length !== concreteSegments.length) return {};
  const params: Record<string, string> = {};
  for (let i = 0; i < templateSegments.length; i += 1) {
    const templateSegment = templateSegments[i]!;
    if (templateSegment.startsWith(":")) {
      const value = concreteSegments[i];
      if (value !== undefined) {
        params[templateSegment.slice(1)] = value;
      }
    }
  }
  return params;
}

/** Human labels for the `:param` names used across the manifest. */
const PATH_PARAM_LABELS: Readonly<Record<string, string>> = {
  key: "Workflow",
  storageId: "Datei",
};

/** Body fields that make a good German one-liner suffix, in priority order. */
const BODY_SALIENT_FIELDS: ReadonlyArray<readonly [string, string]> = [
  ["title", "Titel"],
  ["name", "Name"],
  ["company", "Firma"],
  ["status", "Status"],
  ["kind", "Art"],
  ["type", "Typ"],
];

function salientBodyDetail(body: unknown): string | null {
  if (!isRecord(body)) return null;
  for (const [field, label] of BODY_SALIENT_FIELDS) {
    const value = body[field];
    if (typeof value === "string" && value.trim().length > 0) {
      return `${label}: ${value.trim()}`;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return `${label}: ${String(value)}`;
    }
  }
  return null;
}

/**
 * Produce a German operator one-liner for a concrete `/api/v1` request. Uses
 * the matched endpoint's {@link WerkflowEndpoint.label}, then appends salient
 * params (path `:key` / `:id`, or a notable body field) in prose. Falls back to
 * `METHOD /path` for unknown routes so the caller always gets a usable string.
 */
export function describeWerkflowEndpoint(method: string, path: string, body?: unknown): string {
  const { endpoint } = classifyWerkflowRequest(method, path);
  if (!endpoint) {
    return `${normalizeMethod(method)} ${splitPath(path).length > 0 ? `/${splitPath(path).join("/")}` : path}`;
  }

  const params = extractPathParams(endpoint.pathTemplate, path);
  const suffixes: string[] = [];

  if (params.key !== undefined) {
    suffixes.push(`${PATH_PARAM_LABELS.key} „${params.key}“`);
  }
  if (params.storageId !== undefined) {
    suffixes.push(`${PATH_PARAM_LABELS.storageId} ${params.storageId}`);
  }
  const idValue = params.id;
  if (idValue !== undefined) {
    suffixes.push(`ID ${idValue}`);
  }

  const bodyDetail = salientBodyDetail(body);
  if (bodyDetail !== null) {
    suffixes.push(bodyDetail);
  }

  return suffixes.length > 0 ? `${endpoint.label} (${suffixes.join(", ")})` : endpoint.label;
}
